// RFP step 4: who receives this RFP, and what each of them is asked to quote.
//
// The order of the screen is the point. Pick the GAs first, then pick carriers
// INSIDE each GA's own roster, so a carrier nobody can quote is never offered.
//
// Quote mode keeps Step4SelectCarriers: it has no GAs, so none of this applies.

import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CheckCircle, AlertCircle, Building2, Users } from "lucide-react";
import { Link } from "wouter";
import { cn } from "@/lib/utils";
import { pluralize } from "@/lib/pluralize";
import type { Carrier } from "@shared/schema";
import { useQuoteWizardContext } from "../QuoteWizardContext";
import { toneFor } from "@/lib/quoting/gaTone";
import { GaMark } from "@/lib/quoting/GaMark";
import { CarrierListByLine } from "./CarrierListByLine";
import { useCarrierAvailability } from "./useCarrierAvailability";
import {
  askKey,
  asksForGeneralAgency,
  generalAgenciesAskedFor,
  carrierIdsFromAsks,
} from "@shared/utils/rfpGaCarriers";

type GeneralAgencyRow = {
  id: number;
  name: string;
  isActive?: boolean | null;
  carrierIds?: number[];
};

function SectionHead({
  n,
  title,
  hint,
  right,
}: {
  n: number;
  title: string;
  hint?: string;
  right?: React.ReactNode;
}) {
  return (
    <div className="flex flex-wrap items-center gap-2 mb-2">
      <span className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-foreground text-[11px] font-bold text-background">
        {n}
      </span>
      <span className="text-[15px] font-semibold tracking-tight">{title}</span>
      {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
      <div className="flex-1" />
      {right}
    </div>
  );
}

export function Step4Recipients() {
  const { quoteData, updateQuoteData, directoryContacts, generalAgencies } = useQuoteWizardContext();
  const { entityState, isCarrierQuotable, getCarrierCoverageTypes, getCarriersForLine, getAvailableCarriers, carrierById, toSelectedCarriers } =
    useCarrierAvailability();

  const [activeGaId, setActiveGaId] = useState<number | null>(null);
  // Direct-mode picks, remembered while the broker looks at GA mode. Session
  // only: whichever mode is live is the one that persists, via selectedCarriers.
  const [rememberedDirect, setRememberedDirect] = useState<number[] | null>(null);

  const asks = quoteData.rfpGeneralAgencyAsks ?? [];
  const usingGa = (quoteData.rfpSendVia ?? "carriers") === "ga";
  const pickedGaIds = quoteData.rfpGeneralAgencyIds ?? [];

  const salesContactCount = (generalAgencyId: number) =>
    (directoryContacts || []).filter(
      (contact: any) =>
        contact.generalAgencyId === generalAgencyId &&
        contact.contactType === "sales" &&
        contact.isActive !== false &&
        contact.email,
    ).length;

  const contactsFor = (generalAgencyId: number) =>
    (directoryContacts || []).filter(
      (contact: any) =>
        contact.generalAgencyId === generalAgencyId &&
        contact.contactType === "sales" &&
        contact.isActive !== false &&
        contact.email,
    );

  const activeGeneralAgencies = ((generalAgencies || []) as GeneralAgencyRow[]).filter(
    (generalAgency) => generalAgency.isActive !== false,
  );
  // Properties of the firm, not of this screen, so they do not move as the
  // broker ticks things.
  const usableGeneralAgencies = activeGeneralAgencies.filter(
    (generalAgency) =>
      salesContactCount(generalAgency.id) > 0 && (generalAgency.carrierIds?.length ?? 0) > 0,
  );
  const hasUsableGa = usableGeneralAgencies.length > 0;

  const pickedGeneralAgencies = pickedGaIds
    .map((id) => activeGeneralAgencies.find((generalAgency) => generalAgency.id === id))
    .filter((generalAgency): generalAgency is GeneralAgencyRow => !!generalAgency);

  const activeGeneralAgency =
    pickedGeneralAgencies.find((generalAgency) => generalAgency.id === activeGaId) ??
    pickedGeneralAgencies[0] ??
    null;

  /** Carriers this firm can be asked for: its roster, still subject to the client's state. */
  const rosterFor = (generalAgency: GeneralAgencyRow, coverageType: string): Carrier[] => {
    const roster = new Set(generalAgency.carrierIds ?? []);
    return getAvailableCarriers(coverageType).filter((carrier) => roster.has(carrier.id));
  };

  const rosterCarriers = (generalAgency: GeneralAgencyRow): Carrier[] => {
    const seen = new Map<number, Carrier>();
    for (const coverageType of quoteData.coverageTypes) {
      for (const carrier of rosterFor(generalAgency, coverageType)) seen.set(carrier.id, carrier);
    }
    return Array.from(seen.values());
  };

  // Every write goes through here: asks are the broker's choice, and
  // selectedCarriers is the roll-up the rest of the wizard reads.
  const commitAsks = (nextAsks: string[]) =>
    updateQuoteData({
      rfpGeneralAgencyAsks: nextAsks,
      selectedCarriers: toSelectedCarriers(carrierIdsFromAsks(nextAsks)),
    });

  const toggleAsk = (generalAgencyId: number, carrierId: number) => {
    const key = askKey(generalAgencyId, carrierId);
    commitAsks(asks.includes(key) ? asks.filter((ask) => ask !== key) : [...asks, key]);
  };

  const setWholeRoster = (generalAgency: GeneralAgencyRow, on: boolean) => {
    const ids = rosterCarriers(generalAgency).map((carrier) => carrier.id);
    const withoutThisGa = asks.filter(
      (ask) => !ask.startsWith(`${generalAgency.id}:`),
    );
    commitAsks(on ? [...withoutThisGa, ...ids.map((id) => askKey(generalAgency.id, id))] : withoutThisGa);
  };

  const toggleGeneralAgency = (generalAgencyId: number) => {
    if (pickedGaIds.includes(generalAgencyId)) {
      // Unticking a firm drops what it was asked for: those asks belong to it.
      const nextAsks = asks.filter((ask) => !ask.startsWith(`${generalAgencyId}:`));
      updateQuoteData({
        rfpGeneralAgencyIds: pickedGaIds.filter((id) => id !== generalAgencyId),
        rfpGeneralAgencyAsks: nextAsks,
        selectedCarriers: toSelectedCarriers(carrierIdsFromAsks(nextAsks)),
      });
      if (activeGaId === generalAgencyId) setActiveGaId(null);
    } else {
      updateQuoteData({ rfpGeneralAgencyIds: [...pickedGaIds, generalAgencyId] });
      setActiveGaId(generalAgencyId);
    }
  };

  const setMode = (next: "carriers" | "ga") => {
    if (next === "ga") {
      // Remember the direct picks so coming back does not lose them, then show
      // whatever the asks already say.
      setRememberedDirect(carrierIdsFromSelected());
      updateQuoteData({
        rfpSendVia: "ga",
        selectedCarriers: toSelectedCarriers(carrierIdsFromAsks(asks)),
      });
    } else {
      updateQuoteData({
        rfpSendVia: "carriers",
        selectedCarriers: toSelectedCarriers(rememberedDirect ?? carrierIdsFromSelected()),
      });
    }
  };

  function carrierIdsFromSelected(): number[] {
    const seen = new Set<number>();
    for (const ids of Object.values(quoteData.selectedCarriers ?? {})) {
      for (const id of ids ?? []) seen.add(id);
    }
    return Array.from(seen);
  }

  const toggleDirectCarrier = (carrier: Carrier) => {
    const current = carrierIdsFromSelected();
    const next = current.includes(carrier.id)
      ? current.filter((id) => id !== carrier.id)
      : [...current, carrier.id];
    setRememberedDirect(next);
    updateQuoteData({ selectedCarriers: toSelectedCarriers(next) });
  };

  const askedCarrierIds = carrierIdsFromAsks(asks);
  // Unreachable through the ticked firms. This screen cannot create the state;
  // a draft resumed after a roster changed can still arrive in it.
  const unreachable = usingGa
    ? Array.from(
        new Map(
          quoteData.coverageTypes
            .flatMap((coverageType) => getAvailableCarriers(coverageType))
            .map((carrier) => [carrier.id, carrier]),
        ).values(),
      ).filter(
        (carrier) =>
          !pickedGeneralAgencies.some((generalAgency) =>
            (generalAgency.carrierIds ?? []).includes(carrier.id),
          ) && askedCarrierIds.includes(carrier.id),
      )
    : [];

  return (
    <div className="space-y-5">
      <div className="text-center">
        <h3 className="text-lg font-semibold mb-2">Recipients</h3>
        <p className="text-muted-foreground">Who receives this RFP, and what to ask each of them</p>
        {entityState && (
          <span className="block text-sm mt-1 text-muted-foreground">
            Showing carriers with products available in{" "}
            <Badge variant="outline" className="ml-1">{entityState}</Badge>
          </span>
        )}
      </div>

      {/* 1 — who receives this */}
      <section>
        <SectionHead n={1} title="Who receives this RFP" />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <ModeCard
            selected={!usingGa}
            title="Send to carriers directly"
            description="Goes to every sales contact you have for each carrier"
            icon={Building2}
            onClick={() => setMode("carriers")}
          />
          <ModeCard
            selected={usingGa}
            title="Send through general agencies"
            description={
              hasUsableGa
                ? "Goes to every sales contact at the GAs you pick, listing the carriers you want quoted"
                : "No GA in your directory yet"
            }
            icon={Users}
            disabled={!hasUsableGa}
            onClick={() => setMode("ga")}
            right={
              !hasUsableGa ? (
                <Link href="/directory" className="text-xs underline shrink-0">
                  Add a GA
                </Link>
              ) : undefined
            }
          />
        </div>
      </section>

      {/* 2 — the GAs, or the plain carrier list in direct mode */}
      {usingGa ? (
        <section>
          <SectionHead
            n={2}
            title={usableGeneralAgencies.length === 1 ? "Your general agency" : "Which GAs should quote this?"}
            hint="Each one is asked for its own set of carriers."
            right={
              <Link href="/directory" className="text-xs underline">
                Manage GAs in directory
              </Link>
            }
          />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-2.5">
            {activeGeneralAgencies.map((generalAgency) => {
              const contacts = salesContactCount(generalAgency.id);
              const rosterSize = generalAgency.carrierIds?.length ?? 0;
              const unusable = contacts === 0 || rosterSize === 0;
              const picked = pickedGaIds.includes(generalAgency.id);
              return (
                <button
                  key={generalAgency.id}
                  type="button"
                  disabled={unusable}
                  aria-pressed={picked}
                  onClick={() => toggleGeneralAgency(generalAgency.id)}
                  className={cn(
                    "flex items-start gap-2.5 rounded-lg border p-3 text-left transition-colors",
                    picked ? "border-primary/40 bg-primary/5" : "hover:bg-muted/40",
                    unusable && "opacity-50 cursor-not-allowed hover:bg-transparent",
                  )}
                >
                  <GaMark id={generalAgency.id} name={generalAgency.name} />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1.5">
                      <span className="truncate text-sm font-medium">{generalAgency.name}</span>
                      {picked && <CheckCircle className="h-3.5 w-3.5 shrink-0 text-primary" />}
                    </span>
                    <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                      {contacts === 0
                        ? "no sales contact yet"
                        : rosterSize === 0
                          ? "no carriers linked"
                          : `${contacts} ${contacts === 1 ? "contact" : "contacts"} · works with ${rosterSize} ${pluralize(rosterSize, "carrier")}`}
                    </span>
                  </span>
                  {picked && (
                    <span className="shrink-0 self-center text-[11px] font-semibold text-muted-foreground">
                      {asksForGeneralAgency(asks, generalAgency.id).length} asked
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {usableGeneralAgencies.length < activeGeneralAgencies.length && (
            <p className="mt-2 text-xs text-muted-foreground">
              A greyed-out GA is missing a sales contact or its carrier list. Fix it in the{" "}
              <Link href="/directory" className="underline">Insurance Directory</Link>.
            </p>
          )}
        </section>
      ) : (
        <section>
          <SectionHead
            n={2}
            title="Carriers to quote"
            hint="Each carrier's sales contacts get the lines it was picked for."
          />
          <Card>
            <CardContent className="p-4">
              {/* Same treatment as the GA panes: a carrier that cannot be
                  quoted here is shown and greyed, not dropped. */}
              <CarrierListByLine
                coverageTypes={quoteData.coverageTypes}
                getCarriers={getCarriersForLine}
                isTicked={(carrierId) => carrierIdsFromSelected().includes(carrierId)}
                onToggle={(carrier) => toggleDirectCarrier(carrier)}
                disabledReason={(carrier, coverageType) => {
                  if (carrierIdsFromSelected().includes(carrier.id)) return null;
                  if (isCarrierQuotable(carrier, coverageType)) return null;
                  return entityState ? `Not in ${entityState}` : "Not available";
                }}
              />
            </CardContent>
          </Card>
        </section>
      )}

      {/* 3 — what each GA is asked for */}
      {usingGa && (
        <section>
          <SectionHead
            n={3}
            title={pickedGeneralAgencies.length > 1 ? "What each GA should quote" : "Which carriers to quote"}
            hint={
              pickedGeneralAgencies.length > 1
                ? "One tab per GA. Each tab is what that GA will be asked for."
                : "Only carriers this GA works with are listed."
            }
          />

          {pickedGeneralAgencies.length === 0 ? (
            <div className="rounded-xl border border-dashed px-4 py-7 text-center text-sm text-muted-foreground">
              Tick a GA above to choose carriers.
            </div>
          ) : (
            <div>
              {pickedGeneralAgencies.length > 1 && (
                <Tabs
                  value={String(activeGeneralAgency?.id ?? "")}
                  onValueChange={(value) => setActiveGaId(Number(value))}
                >
                  <TabsList className="mb-2 h-auto flex-wrap justify-start">
                    {pickedGeneralAgencies.map((generalAgency) => {
                      const count = asksForGeneralAgency(asks, generalAgency.id).length;
                      return (
                        <TabsTrigger
                          key={generalAgency.id}
                          value={String(generalAgency.id)}
                          className="gap-2"
                        >
                          <GaMark id={generalAgency.id} name={generalAgency.name} className="h-5 w-5 text-[9px]" />
                          {generalAgency.name}
                          <Badge
                            variant="secondary"
                            className={cn(
                              "px-1.5 py-0 text-[11px] font-semibold",
                              count === 0 &&
                                "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
                            )}
                          >
                            {count === 0 ? "empty" : `${count} ${pluralize(count, "carrier")}`}
                          </Badge>
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </Tabs>
              )}

              {activeGeneralAgency && (
                <GaPane
                  generalAgency={activeGeneralAgency}
                  contacts={contactsFor(activeGeneralAgency.id)}
                  coverageTypes={quoteData.coverageTypes}
                  // Every carrier on the line, not just what this firm can place
                  // here. The rest come through greyed, with the reason.
                  getCarriers={getCarriersForLine}
                  offRoster={(carrier) => !(activeGeneralAgency.carrierIds ?? []).includes(carrier.id)}
                  isQuotableHere={isCarrierQuotable}
                  rosterSize={rosterCarriers(activeGeneralAgency).length}
                  askedIds={asksForGeneralAgency(asks, activeGeneralAgency.id)}
                  onToggle={(carrier) => toggleAsk(activeGeneralAgency.id, carrier.id)}
                  onSetAll={(on) => setWholeRoster(activeGeneralAgency, on)}
                  alsoAskedBy={(carrierId) =>
                    generalAgenciesAskedFor(asks, carrierId)
                      .filter((id) => id !== activeGeneralAgency.id)
                      .map((id) => pickedGeneralAgencies.find((ga) => ga.id === id))
                      .filter((ga): ga is GeneralAgencyRow => !!ga)
                  }
                  entityState={entityState}
                />
              )}
            </div>
          )}
        </section>
      )}

      {/* What the whole RFP adds up to */}
      {usingGa && pickedGeneralAgencies.length > 0 && (
        <SummaryCards
          asks={asks}
          pickedGeneralAgencies={pickedGeneralAgencies}
          carrierById={carrierById}
          unreachable={unreachable}
          activeGeneralAgencies={activeGeneralAgencies}
          onPickGeneralAgency={toggleGeneralAgency}
        />
      )}
    </div>
  );
}

function ModeCard({
  selected,
  title,
  description,
  icon: Icon,
  disabled,
  onClick,
  right,
}: {
  selected: boolean;
  title: string;
  description: string;
  icon: typeof Users;
  disabled?: boolean;
  onClick: () => void;
  right?: React.ReactNode;
}) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-lg border p-3 transition-colors",
        selected ? "border-primary/40 bg-primary/5" : "hover:bg-muted/40",
        disabled ? "opacity-60" : "cursor-pointer",
      )}
      onClick={() => !disabled && onClick()}
    >
      <span
        className={cn(
          "inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg",
          selected ? "bg-primary/10" : "bg-muted",
        )}
      >
        <Icon className={cn("h-4 w-4", selected ? "text-primary" : "text-muted-foreground")} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5 text-sm font-medium">
          {title}
          {selected && <CheckCircle className="h-3.5 w-3.5 shrink-0 text-primary" />}
        </span>
        <span className="block text-[11px] text-muted-foreground">{description}</span>
      </span>
      {right}
    </div>
  );
}

function GaPane({
  generalAgency,
  contacts,
  coverageTypes,
  getCarriers,
  offRoster,
  isQuotableHere,
  rosterSize,
  askedIds,
  onToggle,
  onSetAll,
  alsoAskedBy,
  entityState,
}: {
  generalAgency: GeneralAgencyRow;
  contacts: any[];
  coverageTypes: string[];
  getCarriers: (coverageType: string) => Carrier[];
  /** True for a carrier this firm does not work with: shown, but not tickable. */
  offRoster: (carrier: Carrier) => boolean;
  /** False when the carrier does not write this line where the client is. */
  isQuotableHere: (carrier: Carrier, coverageType: string) => boolean;
  rosterSize: number;
  askedIds: number[];
  onToggle: (carrier: Carrier) => void;
  onSetAll: (on: boolean) => void;
  alsoAskedBy: (carrierId: number) => GeneralAgencyRow[];
  entityState: string | null;
}) {
  const allAsked = rosterSize > 0 && askedIds.length === rosterSize;

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="text-sm">
              {contacts.length === 1 ? "Emails" : `${contacts.length} emails, to`}{" "}
              <span className="font-medium">
                {contacts.length
                  ? contacts.map((contact) => contact.contactName || contact.email).join(" & ")
                  : "nobody yet"}
              </span>
            </p>
            <p className="text-xs text-muted-foreground">
              Works with {rosterSize} {rosterSize === 1 ? "carrier" : "carriers"}
              {entityState ? ` available in ${entityState}` : ""}. {askedIds.length} asked.
            </p>
          </div>
          {rosterSize > 0 && (
            <button
              type="button"
              onClick={() => onSetAll(!allAsked)}
              className="text-xs underline text-muted-foreground hover:text-foreground"
            >
              {allAsked ? "Clear" : "Ask for all"}
            </button>
          )}
        </div>

        {askedIds.length === 0 && (
          <p className="flex items-start gap-1.5 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-400">
            <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-px" />
            This email would ask for nothing. Tick at least one carrier, or untick{" "}
            {generalAgency.name} above.
          </p>
        )}

        <CarrierListByLine
          coverageTypes={coverageTypes}
          getCarriers={getCarriers}
          isTicked={(carrierId) => askedIds.includes(carrierId)}
          onToggle={(carrier) => onToggle(carrier)}
          // An already-asked carrier stays tickable whatever the reason: a draft
          // saved before a roster or a state changed carries stale asks, the
          // send refuses those, so the broker has to be able to untick one.
          //
          // State before roster: no choice of GA fixes a carrier that does not
          // write business where the client is.
          disabledReason={(carrier, coverageType) => {
            if (askedIds.includes(carrier.id)) return null;
            if (!isQuotableHere(carrier, coverageType)) {
              return entityState ? `Not in ${entityState}` : "Not available";
            }
            return offRoster(carrier) ? "Not on their roster" : null;
          }}
          emptyMessage={() => `No carrier writes this line.`}
          renderHint={(carrierId) => {
            const others = alsoAskedBy(carrierId);
            if (!others.length) return null;
            return (
              <span className="flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
                also via
                {others.map((other) => (
                  <span
                    key={other.id}
                    title={other.name}
                    className={cn("h-2 w-2 rounded-full", toneFor(other.id).dot)}
                  />
                ))}
              </span>
            );
          }}
        />
      </CardContent>
    </Card>
  );
}

function SummaryCards({
  asks,
  pickedGeneralAgencies,
  carrierById,
  unreachable,
  activeGeneralAgencies,
  onPickGeneralAgency,
}: {
  asks: string[];
  pickedGeneralAgencies: GeneralAgencyRow[];
  carrierById: Map<number, Carrier>;
  unreachable: Carrier[];
  activeGeneralAgencies: GeneralAgencyRow[];
  onPickGeneralAgency: (id: number) => void;
}) {
  const quotedIds = carrierIdsFromAsks(asks);
  const askedTwice = quotedIds.filter(
    (carrierId) => generalAgenciesAskedFor(asks, carrierId).length > 1,
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      <Card>
        <CardContent className="p-3.5">
          <p className="text-sm font-medium">Everything that will be quoted</p>
          <p className="text-xs text-muted-foreground">
            {quotedIds.length} {quotedIds.length === 1 ? "carrier" : "carriers"} across{" "}
            {pickedGeneralAgencies.length} {pickedGeneralAgencies.length === 1 ? "GA" : "GAs"}
            {askedTwice.length > 0 && ` · ${askedTwice.length} asked of two GAs`}
          </p>
          <div className="mt-2 flex flex-wrap gap-1.5">
            {quotedIds.map((carrierId) => (
              <Badge key={carrierId} variant="outline" className="gap-1 font-normal">
                {pickedGeneralAgencies.length > 1 &&
                  generalAgenciesAskedFor(asks, carrierId).map((generalAgencyId) => (
                    <span
                      key={generalAgencyId}
                      className={cn("h-1.5 w-1.5 rounded-full", toneFor(generalAgencyId).dot)}
                    />
                  ))}
                {carrierById.get(carrierId)?.name ?? `Carrier ${carrierId}`}
              </Badge>
            ))}
            {quotedIds.length === 0 && (
              <span className="text-xs text-muted-foreground">Nothing ticked yet.</span>
            )}
          </div>
        </CardContent>
      </Card>

      {unreachable.length > 0 && (
        <Card>
          <CardContent className="p-3.5">
            <p className="text-sm font-medium">Not reachable through these GAs</p>
            <p className="text-xs text-muted-foreground">
              {unreachable.length} {unreachable.length === 1 ? "carrier" : "carriers"}. Tick another
              GA, or send them directly in a separate RFP.
            </p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {unreachable.map((carrier) => {
                const via = activeGeneralAgencies.find(
                  (generalAgency) =>
                    !pickedGeneralAgencies.some((picked) => picked.id === generalAgency.id) &&
                    (generalAgency.carrierIds ?? []).includes(carrier.id),
                );
                return (
                  <Badge
                    key={carrier.id}
                    variant="outline"
                    className="gap-1.5 border-dashed font-normal text-muted-foreground"
                  >
                    {carrier.name}
                    {via && (
                      <button
                        type="button"
                        onClick={() => onPickGeneralAgency(via.id)}
                        className="underline"
                      >
                        via {via.name.split(" ")[0]}
                      </button>
                    )}
                  </Badge>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
